
import { computed, defineComponent } from "vue";
import { useStore } from "vuex";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import moment from "moment";
import ApiService from "@/core/services/ApiService";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { MachineType } from "@/types/MachineType";
import { useI18n } from "vue-i18n/index";
import { hasFeature } from "@/core/helpers/feature";
import posthog from '@/posthog';

export default defineComponent({
  name: "machine_card",
  data() {
    return {
      release_countdown: "",
      release_countdown_started: false,
      countdown: "",
      countdown_started: false,
      loading: false,
      flag_input: "",
      stopTimestamp: null as number | null,
      stopTimeoutInterval: null as ReturnType<typeof setInterval> | null,
      cooldownSeconds: null as number | null,
      globalCooldownCheckInterval: null as ReturnType<typeof setInterval> | null,
      descriptionBlurred: true,
    };
  },
  props: {
    machine: {
      type: Object as () => MachineType,
      default: null,
    },
    type: {
      type: String,
      default: "default",
    },
  },
  computed: {
    isStartButtonDisabled() {
      return this.cooldownSeconds !== null && this.cooldownSeconds > 0;
    },
    remainingCooldownTime() {
      return this.cooldownSeconds;
    }
  },
  setup(props) {
    const store = useStore();
    const { t } = useI18n();
    const currentLanguage = computed(() => store.getters.getCurrentLanguage || localStorage.getItem('currentLanguage'));

    const user = computed(() => {
      return store.getters.currentUser;
    });

    return {
      moment,
      user,
      store,
      currentLanguage,
      Actions,
      hasFeature
    };
  },
  created() {
    if (!this.countdown_started) {
      if (this.machine.instance) {
        setInterval(this.update, 1000);
        this.countdown_started = true;
      }
    }

    if (!this.release_countdown_started) {
      if (this.machine) {
        setInterval(this.update_release_countdown, 1000);
        this.release_countdown_started = true;
      }
    }

    this.loadCooldownFromStorage();

    window.addEventListener('storage', this.handleStorageChange);

    this.globalCooldownCheckInterval = setInterval(() => {
      this.checkGlobalCooldown();
    }, 1000);
  },
  watch: {
    'machine.instance.is_active'(newValue) {
      // If machine becomes active, clear the cooldown
      if (newValue === true && this.stopTimestamp) {
        this.stopTimestamp = null;
        this.cooldownSeconds = null;
        this.clearCooldownFromStorage();
        this.clearCooldownTimer();
      }
    }
  },
  methods: {
    update_release_countdown: function () {

      if (this.machine.release_at) {
        const now = moment.utc();
        const end = moment.utc(this.machine.release_at);
        const duration = moment.duration(end.diff(now));
        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();
        const days = duration.days();

        if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
          this.release_countdown = '';
        } else {
          this.release_countdown = `${days}d ${hours}h ${minutes}m ${seconds}s`;
        }

      }
    },
    update: function (machine) {
      const now = moment.utc();
      const end = machine ? moment.utc(machine.instance.shutdown) : moment.utc(this.machine.instance.shutdown);

      const duration = moment.duration(end.diff(now));
      const hours = duration.hours();
      const minutes = duration.minutes();
      const seconds = duration.seconds();


      if (hours <= 0 && minutes <= 1 && seconds == 59) {
        // play alert sound
        this.play_alert_sound();
      }

      if (hours <= 0 && minutes <= 0 && seconds == 20) {
        // play alert sound
        this.play_alert_sound();
      }
      if (hours <= 0 && minutes <= 0 && seconds <= 0) {
        this.countdown = '';

      } else {
        this.countdown = `${hours}h ${minutes}m ${seconds}s`;
      }

    },
    StopInstance: function () {
      if (this.loading) {
        return;
      }

      this.loading = true;
      ApiService.get("labs/machines/" + this.$props.machine.id + "/stop")
        .then(({ data }) => {

          if (data.success) {
            this.store.commit(Mutations.SET_CURRENT_MACHINE, data.machine);
            // Set timestamp when machine is stopped successfully
            this.stopTimestamp = Date.now();
            this.cooldownSeconds = 60;
            this.saveCooldownToStorage();
            this.startCooldownTimer();
            this.loading = false;
          } else {
            Swal.fire({
              title: "Oops...",
              text: data.message,
              icon: "error",
              confirmButtonText: "Ok",
            });
            this.store.commit(Mutations.SET_CURRENT_MACHINE, data.machine);
            this.loading = false;
          }

        }).catch(error => {

          Swal.fire({
            title: "Oops...",
            text: this.$t(`${this.currentLanguage}.messages.we_had_a_problem_closing_your_instance_contact_support`),
            icon: "error",
            confirmButtonText: "Ok",
          });
          this.loading = false;
        });
    },
    handleStartClick() {
      if (this.isStartButtonDisabled) {
        const cooldownTitle = this.$t(`${this.currentLanguage}.machine.cooldown_active`);
        const cooldownMessage = this.$t(`${this.currentLanguage}.machine.cooldown_message`);
        const cooldownInfo = this.$t(`${this.currentLanguage}.machine.cooldown_info`);
        const closeText = this.$t(`${this.currentLanguage}.common.close`);
        const timeRemaining = this.formatCooldownTime(this.remainingCooldownTime || 0);

        Swal.fire({
          title: `<i class="bi bi-hourglass-split text-warning me-2"></i> ${cooldownTitle}`,
          html: `
            <p class="fs-5 mb-4">${cooldownMessage}</p>
            <div class="d-flex align-items-center justify-content-center mb-3">
              <i class="bi bi-hourglass-split text-warning me-2" style="font-size: 2rem;"></i>
              <span class="cooldown-timer fs-2 fw-bold" style="color: #000000;">${timeRemaining}</span>
            </div>
            <p class="text-muted">${cooldownInfo}</p>
          `,
          icon: 'info',
          confirmButtonText: closeText,
          confirmButtonColor: '#6c757d'
        });
        return;
      }
      this.StartInstance();
    },
    StartInstance: function () {
      if (this.loading || this.isStartButtonDisabled) {
        return;
      }
      this.loading = true;
      ApiService.get("labs/machines/" + this.$props.machine.id + "/start")
        .then(({ data }) => {

          if (data.success) {
            this.store.commit(Mutations.SET_CURRENT_MACHINE, data.machine);
            this.stopTimestamp = null;
            this.cooldownSeconds = null;
            this.clearCooldownFromStorage();
            this.clearCooldownTimer();
            posthog.capture('started_machine_instance', {
              id: this.$props.machine.id,
              name: data.machine.name,
              is_freemium: data.machine.is_freemium,
              difficulty: data.machine.dificulty,
              release_at: data.machine.release_at,
              creator: data.machine.creator?.id
            });

            this.loading = false;
            this.countdown_started = true;
            setInterval(() => {
              if (this.machine.instance) {
                this.update(this.machine);
              } else {
                this.update(data.machine);;
              }
            }, 1000);

          } else {
            Swal.fire({
              title: "Oops...",
              text: data.message,
              icon: "error",
              confirmButtonText: "Ok",
            });
            this.loading = false;
          }

        }).catch(error => {


          if (error.response.data.message) {
            Swal.fire({
              title: "Oops...",
              text: error.response.data.message,
              icon: "warning",
              confirmButtonText: "Ok",
            });
          } else {
            Swal.fire({
              title: "Oops...",
              text: this.$t(`${this.currentLanguage}.messages.we_had_a_problem_starting_your_instance_please_try_again_later`),
              icon: "error",
              confirmButtonText: "Ok",
            });
          }
          this.loading = false;
        });

    },
    submitFlag() {
      if (!this.flag_input) {
        Swal.fire({
          title: "Oops...",
          text: this.$t(`${this.currentLanguage}.messages.please_insert_a_flag`),
          icon: "error",
          confirmButtonText: "Ok",
        });
        return;
      }
      ApiService.vueInstance.axios.put("/labs/machines/pown/" + this.machine.id, {
        flag: this.flag_input
      }).then(async ({ data }) => {
        if (data.success) {
          await this.store.dispatch(Actions.GET_MACHINE, { id: this.machine.id })
          await this.store.dispatch(Actions.GET_MACHINE_ACTIVITY, { id: this.machine.id });
          const isAlreadySent = data.message && (data.message.toLowerCase().includes('já enviou') || data.message.toLowerCase().includes('already sent'));
          if (isAlreadySent) {
            Swal.fire({
              title: this.$t(`${this.currentLanguage}.messages.congratulations`),
              text: data.message,
              icon: "info",
              confirmButtonText: "Ok",
            });
          } else if (data.completed) {
            // Máquina completada — o modal de conquista cuida da celebração.
          } else {
            Swal.fire({
              title: this.$t(`${this.currentLanguage}.messages.congratulations`),
              text: this.$t(`${this.currentLanguage}.messages.you_hit_the_flag`),
              icon: "success",
              confirmButtonText: "Ok",
            });
          }
        } else {
          Swal.fire({
            title: "Oops...",
            text: data.message,
            icon: "error",
            confirmButtonText: "Ok",
          });
        }
      }).catch(error => {
        Swal.fire({
          title: "Oops...",
          text: this.$t(`${this.currentLanguage}.messages.we_had_a_problem_verifying_your_flag_please_try_again`),
          icon: "error",
          confirmButtonText: "Ok",
        });
      });

    },
    addTime() {
      // add time to this machine
      if (this.loading) {
        return;
      }
      this.loading = true;
      ApiService.get("labs/machines/" + this.$props.machine.id + "/addTime")
        .then(({ data }) => {

          if (data.success) {

            Swal.fire({
              title: this.$t(`${this.currentLanguage}.messages.congratulations`),
              text: this.$t(`${this.currentLanguage}.messages.you_ve_added_more_time_to_your_instance`),
              icon: "success",
              confirmButtonText: "Ok",
            });

            this.store.commit(Mutations.SET_CURRENT_MACHINE, data.machine);
            this.loading = false;
          } else {
            Swal.fire({
              title: "Oops...",
              text: data.message,
              icon: "error",
              confirmButtonText: "Ok",
            });
            this.loading = false;
          }

        }).catch(error => {

          Swal.fire({
            title: "Oops...",
            text: this.$t(`${this.currentLanguage}.messages.we_had_a_problem_adding_time_to_your_instance_please_try_again_later`),
            icon: "error",
            confirmButtonText: "Ok",
          });
          this.loading = false;
        });
    },
    play_alert_sound() {
      const audio = new Audio('/media/alert.mp3');
      audio.play();
    },
    startCooldownTimer() {
      // Clear any existing timer
      this.clearCooldownTimer();

      // Update every second to show countdown
      this.stopTimeoutInterval = setInterval(() => {
        if (this.stopTimestamp) {
          const elapsed = Math.floor((Date.now() - this.stopTimestamp) / 1000);
          const remaining = 60 - elapsed;

          if (remaining > 0) {
            // Update the reactive cooldown seconds
            this.cooldownSeconds = remaining;
          } else {
            // Cooldown period is over
            this.stopTimestamp = null;
            this.cooldownSeconds = null;
            this.clearCooldownFromStorage();
            this.clearCooldownTimer();
          }
        }
      }, 1000);
    },
    clearCooldownTimer() {
      if (this.stopTimeoutInterval) {
        clearInterval(this.stopTimeoutInterval);
        this.stopTimeoutInterval = null;
      }
    },
    getCooldownStorageKey() {
      return `machine_cooldown_global`;
    },
    saveCooldownToStorage() {
      if (this.stopTimestamp) {
        localStorage.setItem(this.getCooldownStorageKey(), this.stopTimestamp.toString());
      }
    },
    loadCooldownFromStorage() {
      const storageKey = this.getCooldownStorageKey();
      const storedTimestamp = localStorage.getItem(storageKey);

      if (storedTimestamp) {
        const timestamp = Number.parseInt(storedTimestamp, 10);
        const elapsed = Math.floor((Date.now() - timestamp) / 1000);
        const remaining = 60 - elapsed;

        if (remaining > 0) {
          // Cooldown is still active
          this.stopTimestamp = timestamp;
          this.cooldownSeconds = remaining;
          this.startCooldownTimer();
        } else {
          // Cooldown has expired, clean up
          this.clearCooldownFromStorage();
        }
      }
    },
    clearCooldownFromStorage() {
      localStorage.removeItem(this.getCooldownStorageKey());
    },
    formatCooldownTime(seconds: number) {
      return `${seconds}s`;
    },
    checkGlobalCooldown() {
      const storageKey = this.getCooldownStorageKey();
      const storedTimestamp = localStorage.getItem(storageKey);

      if (storedTimestamp) {
        const timestamp = Number.parseInt(storedTimestamp, 10);
        const elapsed = Math.floor((Date.now() - timestamp) / 1000);
        const remaining = 60 - elapsed;

        if (remaining > 0) {
          if (!this.stopTimestamp || this.stopTimestamp !== timestamp) {
            this.stopTimestamp = timestamp;
            this.cooldownSeconds = remaining;
            if (!this.stopTimeoutInterval) {
              this.startCooldownTimer();
            }
          }
        } else {
          if (this.stopTimestamp) {
            this.stopTimestamp = null;
            this.cooldownSeconds = null;
            this.clearCooldownTimer();
          }
          this.clearCooldownFromStorage();
        }
      } else {
        if (this.stopTimestamp) {
          this.stopTimestamp = null;
          this.cooldownSeconds = null;
          this.clearCooldownTimer();
        }
      }
    },
    handleStorageChange(event: StorageEvent) {
      if (event.key === this.getCooldownStorageKey()) {
        this.checkGlobalCooldown();
      }
    }
  },
  beforeUnmount() {
    this.clearCooldownTimer();
    if (this.globalCooldownCheckInterval) {
      clearInterval(this.globalCooldownCheckInterval);
      this.globalCooldownCheckInterval = null;
    }
    window.removeEventListener('storage', this.handleStorageChange);
  },
});
